import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { trpc } from '@/lib/trpc';
import { CANONICAL_BOOKMAKERS, findCanonicalBookmaker } from '@tennis-scanner/shared';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';

interface Props {
  open: boolean;
  onClose: () => void;
  // Books already in scanner_config.allowedBooks — filtered out of the
  // picker. Optional so callers that don't have the list still work
  // (form just shows the full canonical list).
  existingBooks?: readonly string[];
}

// Modal for config.addBook. Operator picks from CANONICAL_BOOKMAKERS (the
// books api-tennis emits after normalize.ts maps vendor names) via a
// datalist-backed input — native autocomplete, no extra deps. Free-text
// is still allowed for forward-compat with new books not yet catalogued.
export function AddBookDialog({ open, onClose, existingBooks = [] }: Props) {
  const utils = trpc.useUtils();
  const [book, setBook] = useState('');
  const [priority, setPriority] = useState('5');
  const [type, setType] = useState<'sharp' | 'soft'>('soft');
  const [notes, setNotes] = useState('');

  const existingSet = useMemo(
    () => new Set(existingBooks.map((b) => b.toLowerCase())),
    [existingBooks],
  );
  const available = useMemo(
    () => CANONICAL_BOOKMAKERS.filter((b) => !existingSet.has(b.slug)),
    [existingSet],
  );

  useEffect(() => {
    if (open) {
      setBook('');
      setPriority('5');
      setType('soft');
      setNotes('');
    }
  }, [open]);

  // When the operator picks a known canonical book, pre-fill the type
  // (sharp / soft) so the form ends up correct without extra clicks.
  useEffect(() => {
    const match = findCanonicalBookmaker(book);
    if (match) setType(match.defaultType);
  }, [book]);

  const mutation = trpc.config.addBook.useMutation({
    onSuccess: async () => {
      toast.success(`Book ${book.trim()} agregado`);
      await utils.config.get.invalidate();
      onClose();
    },
    onError: (err) => toast.error('No se pudo agregar', { description: err.message }),
  });

  const slugTrimmed = book.trim().toLowerCase();
  const priorityNum = Number.parseInt(priority, 10);
  const alreadyAdded = slugTrimmed.length > 0 && existingSet.has(slugTrimmed);
  const canSubmit =
    slugTrimmed.length > 0 &&
    !alreadyAdded &&
    Number.isInteger(priorityNum) &&
    priorityNum >= 1 &&
    priorityNum <= 999;

  function handleSubmit(e: React.FormEvent): void {
    e.preventDefault();
    if (!canSubmit) return;
    const meta: Record<string, unknown> = { type };
    if (notes.trim()) meta.notes = notes.trim();
    mutation.mutate({ book: slugTrimmed, priority: priorityNum, metadata: meta });
  }

  const matched = findCanonicalBookmaker(book);
  const isCustom = book.trim().length > 0 && !matched;

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Agregar casa de apuestas</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="grid gap-4">
          <div className="grid gap-1.5">
            <Label htmlFor="add-book">Casa</Label>
            <Input
              id="add-book"
              value={book}
              onChange={(e) => setBook(e.target.value)}
              placeholder="Empezá a escribir o elegí de la lista…"
              list="canonical-bookmakers"
              autoComplete="off"
              autoFocus
            />
            <datalist id="canonical-bookmakers">
              {available.map((b) => (
                <option key={b.slug} value={b.slug}>
                  {b.displayName} · {b.defaultType}
                </option>
              ))}
            </datalist>
            <p className="text-[11px] text-muted-foreground">
              {matched ? (
                <>
                  <span className="text-foreground">{matched.displayName}</span> · tipo pre-llenado:{' '}
                  <code className="font-mono">{matched.defaultType}</code>
                </>
              ) : isCustom ? (
                <span className="text-warning">
                  Casa fuera del catálogo conocido — solo aparecerá si api-tennis la emite con ese
                  mismo slug.
                </span>
              ) : (
                <>{available.length} casas conocidas disponibles del API</>
              )}
            </p>
            {alreadyAdded && (
              <p className="text-[11px] text-destructive">Esta casa ya está agregada.</p>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="grid gap-1.5">
              <Label htmlFor="add-prio">Prioridad (1-999)</Label>
              <Input
                id="add-prio"
                type="number"
                step="1"
                min="1"
                max="999"
                value={priority}
                onChange={(e) => setPriority(e.target.value)}
              />
            </div>
            <div className="grid gap-1.5">
              <Label htmlFor="add-type">Tipo</Label>
              <Select value={type} onValueChange={(v) => setType(v as 'sharp' | 'soft')}>
                <SelectTrigger id="add-type">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="sharp">sharp</SelectItem>
                  <SelectItem value="soft">soft</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid gap-1.5">
            <Label htmlFor="add-notes">Notas (opcional)</Label>
            <Input id="add-notes" value={notes} onChange={(e) => setNotes(e.target.value)} />
          </div>
          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose}>
              Cancelar
            </Button>
            <Button type="submit" disabled={!canSubmit || mutation.isPending}>
              {mutation.isPending ? 'Agregando…' : 'Agregar'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
